import { useMemo } from 'react';
import {
	AllCommunityModule,
	ModuleRegistry,
	themeMaterial,
	colorSchemeDark,
	ColDef,
	RowSelectionOptions,
	SelectionChangedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { getColorGradient } from '../utils/helpers';

/* Register all Community features */
ModuleRegistry.registerModules([AllCommunityModule]);

type Props = {
  measureNames: string[];
	actualScores: number[];
	percentileRanks: number[];
	selectionEnabled: boolean;
	correlationVisible: boolean;
	correlationPercents: number[];
	onGridReady?: (params: any) => void;
	onSelectionChanged?: (event: SelectionChangedEvent) => void;
};

/**
 * Tests table component
 * 
 * @param tests Array of test rows
 * @param selectionEnabled Whether selection is enabled
 * @returns Tests table element
 */
export const MeasuresTable = (
	{ 
		measureNames,
		actualScores,
		percentileRanks,
		selectionEnabled,
		correlationVisible,
		correlationPercents,
		onGridReady,
		onSelectionChanged,
	}: Props): JSX.Element => {
	/**
	 * Returns a color gradient based on the value of the cell
	 * 
	 * @param params Range [0-100] percent value
	 * @returns CellStyle object for coloring the cell and text
	 */
	
	/* Column definitions */
	const columnDefs: ColDef[] = 
		correlationVisible ?
			[
		    { field: "Measure", width: 400 },
		    { field: "Positive Diagnosis Correlation", flex: 1.2, type: 'rightAligned' },
		    { field: "Score", flex: selectionEnabled ? 0.7 : 0.9, type: 'rightAligned' },
		    { field: "Percentile", flex: selectionEnabled ? 1 : 1.1, cellStyle: params => getColorGradient(params.value), type: 'rightAligned' },
		  ] :
		  [
		    { field: "Measure", width: 500 },
		    { field: "Score", flex: selectionEnabled ? 0.5 : 0.75, type: 'rightAligned' },
		    { field: "Percentile", flex: selectionEnabled ? 0.5 : 0.75, cellStyle: params => getColorGradient(params.value), type: 'rightAligned' },
		  ];

	/* Defaults settings for all columns */
	const defaultColDef: ColDef = {
		filter: true,
		cellClass: 'tableContents',
		headerClass: 'tableHeader',
		wrapHeaderText: true,
		autoHeaderHeight: true,
	};

	/* List of measures client took (memoized to avoid reload since they don't change) */
	const rowData = useMemo(() => {
		const unsortedData = measureNames.map((measureName: string, i: number) => {
			if (correlationVisible) {
				return {
					"Measure": measureName,
					"Positive Diagnosis Correlation": correlationPercents[i] ? correlationPercents[i].toFixed(2) : null,
					"Score": actualScores[i],
					"Percentile": percentileRanks[i] ?? null,
				};
			} else {
				return {
					"Measure": measureName,
					"Score": actualScores[i],
					"Percentile": percentileRanks[i] ?? null,
				};			
			}
		});
		if (unsortedData.length && !correlationVisible) {
			return unsortedData.sort((a, b) => {
				return (a["Percentile"] ?? Number.POSITIVE_INFINITY) - (b["Percentile"] ?? Number.POSITIVE_INFINITY);
			});
		} else if (unsortedData.length) {
			return unsortedData.sort((a, b) => {
				return (Number(b["Positive Diagnosis Correlation"]) ?? Number.POSITIVE_INFINITY) - (Number(a["Positive Diagnosis Correlation"]) ?? Number.POSITIVE_INFINITY);
			});
		}
	}, [correlationVisible, measureNames, actualScores, correlationPercents, percentileRanks]);

	/* Table aesthetic settings */
	const tableTheme = themeMaterial
		.withPart(colorSchemeDark)
		.withParams({
    	// headerTextColor: 'white',
    	// fontWeight: 'normal',
    	// fontSize: '14pt',
    	accentColor: '#3a86ff',
    });

	/* Table row selection details */
  const rowSelection: RowSelectionOptions = {
    mode: 'multiRow',
    checkboxes: true,
    headerCheckbox: true,
    enableClickSelection: false,
  };

  return (
    <div className={correlationVisible ? "diagnosisSpecificTableContainer" : "tableContainer"}>
			<AgGridReact
				domLayout='autoHeight'
				defaultColDef={defaultColDef}
				rowData={rowData}
				columnDefs={columnDefs}
				theme={tableTheme}
				rowSelection={selectionEnabled ? rowSelection : undefined}
				onGridReady={onGridReady}
				onSelectionChanged={onSelectionChanged}
			/>
    </div>
	);
};

export default MeasuresTable;
